import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RefreshCcw } from 'lucide-react';

import PhaseIndicator from '../components/job-brief/PhaseIndicator';
import ReadinessScores from '../components/job-brief/ReadinessScores';
import SaveStateIndicator from '../components/job-brief/SaveStateIndicator';
import PlanPhase from '../components/job-brief/plan/PlanPhase';
import PerformPhase from '../components/job-brief/perform/PerformPhase';
import CloseoutPhase from '../components/job-brief/closeout/CloseoutPhase';
import { jobBriefV2Styles } from '../components/job-brief/styles';
import { jobBriefApi } from '../services/jobBriefApi';
import { computeReadiness } from '../config/jobBriefRules';

const AUTOSAVE_DELAY_MS = 1200;

/**
 * JobBriefV2Editor — /job-briefing/v2/:id
 *
 * Owns the shell for a single job brief:
 *   - loads + rehydrates from the v2 API
 *   - autosaves title/phase/section edits (1.2s debounce)
 *   - drives Phase, Readiness, and SaveState indicators
 *   - swaps between the Plan/Perform/CloseOut placeholders
 *   - triggers rebrief (with reason capture) → immutable revision
 */
export default function JobBriefV2Editor({ user, onLogout }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveState, setSaveState] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const skipSaveRef = useRef(true);

  // Initial load & rehydration ─────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const d = await jobBriefApi.getDraft(id);
        if (cancelled) return;
        setDraft(d);
        setLoadError('');
        setSaveState('saved');
        setLastSavedAt(d?.updated_at ? new Date(d.updated_at) : new Date());
        skipSaveRef.current = true;               // don't autosave on the first render
      } catch (e) {
        if (!cancelled) setLoadError('Could not load this draft.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const reloadDraft = useCallback(async () => {
    if (!id) return;
    try {
      const d = await jobBriefApi.getDraft(id);
      skipSaveRef.current = true;
      setDraft(d);
      setSaveState('saved');
      setLastSavedAt(d?.updated_at ? new Date(d.updated_at) : new Date());
    } catch { /* keep existing draft */ }
  }, [id]);

  // Readiness ── derived from the live draft, kept in sync via autosave.
  const readiness = useMemo(() => computeReadiness(draft || {}), [draft]);

  // Autosave (1.2s debounce) ───────────────────────────────────────────
  useEffect(() => {
    if (skipSaveRef.current || !draft?.id) {
      skipSaveRef.current = false;
      return;
    }
    setSaveState((s) => (s === 'saving' ? s : 'dirty'));
    const t = setTimeout(async () => {
      setSaveState('saving');
      try {
        const patch = {
          title:           draft.title,
          phase:           draft.phase,
          job_information: draft.job_information,
          conversational:  draft.conversational,
          scope:           draft.scope,
          hazards:         draft.hazards,
          energy_sources:  draft.energy_sources,
          error_traps:     draft.error_traps,
          hp_tools:        draft.hp_tools,
          critical_steps:  draft.critical_steps,
          hold_points:     draft.hold_points,
          ppe:             draft.ppe,
          clearances:      draft.clearances,
          grounding:       draft.grounding,
          lift_plan:       draft.lift_plan,
          weather:         draft.weather,
          fatigue:         draft.fatigue,
          emergency:       draft.emergency,
          documents:       draft.documents,
          quality:         draft.quality,
          crew:            draft.crew,
          acknowledgments: draft.acknowledgments,
          readiness,
        };
        const updated = await jobBriefApi.updateDraft(draft.id, patch);
        setSaveState('saved');
        setLastSavedAt(updated?.updated_at ? new Date(updated.updated_at) : new Date());
      } catch {
        setSaveState('error');
      }
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(t);
    // We intentionally depend on the entire draft object — cheap and correct for autosave.
  }, [draft, readiness]);

  // Handlers ───────────────────────────────────────────────────────────
  const handlePhaseChange = useCallback((next) => {
    setDraft((d) => (d ? { ...d, phase: next } : d));
  }, []);

  const handleTitleChange = useCallback((v) => {
    setDraft((d) => (d ? { ...d, title: v } : d));
  }, []);

  // Plan-phase section change handler — sections call this with a partial
  // patch (e.g. {hazards: [...]}) and we merge into the live draft.
  const handleSectionPatch = useCallback((patch) => {
    if (!patch) return;
    setDraft((d) => (d ? { ...d, ...patch } : d));
  }, []);

  const handleRebrief = useCallback(async () => {
    if (!draft?.id) return;
    const reason = window.prompt('Reason for rebrief (short summary)?');
    if (!reason || reason.trim().length < 3) return;
    const updated = await jobBriefApi.rebrief(draft.id, { reason: reason.trim() });
    if (updated?.id) setDraft(updated);
  }, [draft?.id]);

  // Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="jbv2-root" data-testid="jbv2-editor-loading">
        <style>{jobBriefV2Styles}</style>
        <main className="jbv2-main"><p style={{ color: '#94a3b8' }}>Loading draft…</p></main>
      </div>
    );
  }

  if (loadError || !draft) {
    return (
      <div className="jbv2-root" data-testid="jbv2-editor-error">
        <style>{jobBriefV2Styles}</style>
        <main className="jbv2-main">
          <p style={{ color: '#fecaca' }}>{loadError || 'Draft not found.'}</p>
          <button
            type="button"
            className="jbv2-header-back"
            onClick={() => navigate('/job-briefing/v2')}
            data-testid="jbv2-editor-back-to-home"
          >
            Back to Job Brief home
          </button>
        </main>
      </div>
    );
  }

  const phase = draft.phase || 'plan';

  return (
    <div className="jbv2-root" data-testid="jbv2-editor">
      <style>{jobBriefV2Styles}</style>

      <main className="jbv2-main">
        {/* Title + save state toolbar */}
        <div className="jbv2-toolbar">
          <div style={{ flex: 1, minWidth: 260 }}>
            <div className="jbv2-h-eyebrow" style={{ marginBottom: 4 }}>Draft · Revision {draft.revision || 1}</div>
            <input
              type="text"
              className="jbv2-title-input"
              value={draft.title || ''}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Untitled Job Brief"
              data-testid="jbv2-title-input"
              aria-label="Job Brief title"
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <SaveStateIndicator state={saveState} lastSavedAt={lastSavedAt} />
            <button
              type="button"
              className="jbv2-header-back"
              onClick={handleRebrief}
              data-testid="jbv2-rebrief"
              aria-label="Trigger rebrief"
              title="Trigger a rebrief (saves an immutable snapshot before continuing)"
            >
              <RefreshCcw size={13} strokeWidth={2.4} />
              <span>Rebrief</span>
            </button>
          </div>
        </div>

        <PhaseIndicator current={phase} onChange={handlePhaseChange} />
        <ReadinessScores readiness={readiness} />

        {phase === 'plan'     && <PlanPhase                draft={draft} onChange={handleSectionPatch} />}
        {phase === 'perform'  && <PerformPhase             draft={draft} onReload={reloadDraft} />}
        {phase === 'closeout' && <CloseoutPhase             draft={draft} />}
      </main>
    </div>
  );
}
